/**
 * Resolve the landing-page host based on the snippet generation environment.
 * Mirrors logic in views/embed.hbs:62-70 so staging-generated snippets work
 * in staging and local development.
 *
 * @returns {string} The base URL prefix for landing pages, e.g. "https://landing.dojomojo.com".
 */
export function resolveLandingBase() {
  if (typeof window === "undefined") {
    return "https://landing.dojomojo.com";
  }
  const host = window.location.host;
  if (host === "www.dojodevmojo.com") {
    return "https://www.dojodevmojo.com";
  }
  if (host === "localhost:3001") {
    return "http://localhost:3001";
  }
  return "https://landing.dojomojo.com";
}

/**
 * Compose the inline style string shared by both the HTML and React snippet builders.
 * Uses `overflow: hidden` to suppress scrollbars when scrolling is disabled, since
 * the HTML `scrolling` attribute is deprecated in HTML5.
 *
 * @param {Object} options
 * @param {string} options.width
 * @param {number} options.height
 * @param {boolean} options.border
 * @param {boolean} options.scrolling
 * @returns {string} CSS declarations joined by ";".
 */
function composeIframeStyle(options) {
  const { width, height, border, scrolling } = options;
  const entries = [
    `width:${width}`,
    `height:${height}px`,
    `border:${border ? "1px solid #ccc" : "0"}`,
    "display:block",
  ];
  if (!scrolling) {
    entries.push("overflow:hidden");
  }
  return entries.join(";");
}

/**
 * Build the iframe-only HTML embed snippet for a self-hosted campaign.
 * The snippet includes a small inline script that propagates the parent
 * page's query string into the iframe src so `promo_id` is preserved for
 * participating-brand attribution (mirrors scripts/util/embed-code.js:64).
 *
 * @param {Object} options
 * @param {string} options.campaignUid - Campaign UID (campaign.uid), used in the iframe URL path.
 * @param {string} [options.width="100%"] - Width with unit included, e.g. "100%" or "600px".
 * @param {number} [options.height=800] - Height in pixels.
 * @param {boolean} [options.border=false] - Whether to render a 1px border around the iframe.
 * @param {boolean} [options.scrolling=true] - Whether the iframe allows scrolling.
 * @returns {string} HTML string containing one `<iframe>` and one `<script>` tag.
 */
export function buildIframeSnippet(options) {
  const { campaignUid, width = "100%", height = 800, border = false, scrolling = true } = options;

  const base = resolveLandingBase();
  const landerUrl = `${base}/landing/campaign/${campaignUid}`;
  const iframeId = `dojomojo-sweeps-${campaignUid}`;
  const styleStr = composeIframeStyle({ width, height, border, scrolling });

  return `<iframe id="${iframeId}"
  src="${landerUrl}"
  style="${styleStr}"
  title="DojoMojo Sweepstakes"></iframe>
<script>
{
  const f = document.getElementById("${iframeId}");
  if (f) {
    const qs = window.location.search || "";
    f.src = "${landerUrl}" + qs;
  }
}
</script>`;
}

/**
 * Build a React component source snippet for the self-hosted iframe. The
 * component is SSR-safe: it renders the bare URL on first render so server
 * markup matches the client, then patches the iframe `src` inside `useEffect`
 * to forward `window.location.search` (preserving `promo_id` attribution).
 *
 * @param {Object} options
 * @param {string} options.campaignUid - Campaign UID used in the iframe src.
 * @param {string} [options.width="100%"] - Width with unit included, e.g. "100%" or "600px".
 * @param {number} [options.height=800] - Height in pixels.
 * @param {boolean} [options.border=false] - Whether to render a 1px border.
 * @param {boolean} [options.scrolling=true] - Whether the iframe allows scrolling.
 * @returns {string} JSX component source code (named export, no default).
 */
export function buildReactSnippet(options) {
  const { campaignUid, width = "100%", height = 800, border = false, scrolling = true } = options;

  const base = resolveLandingBase();
  const landerUrl = `${base}/landing/campaign/${campaignUid}`;
  const styleProps = [
    `width: "${width}"`,
    `height: ${height}`,
    `border: ${border ? '"1px solid #ccc"' : 0}`,
    `display: "block"`,
  ];
  if (!scrolling) {
    styleProps.push(`overflow: "hidden"`);
  }
  const styleStr = `{ ${styleProps.join(", ")} }`;

  return `import { useEffect, useRef } from "react";

export function DojoMojoSweepstakes() {
  const iframeRef = useRef(null);
  const baseSrc = "${landerUrl}";

  useEffect(() => {
    if (iframeRef.current && typeof window !== "undefined") {
      iframeRef.current.src = baseSrc + (window.location.search || "");
    }
  }, []);

  return (
    <iframe
      ref={iframeRef}
      title="DojoMojo Sweepstakes"
      src={baseSrc}
      style={${styleStr}}
    />
  );
}`;
}
